import React, { useCallback, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import {
  BadgePercent,
  CalendarRange,
  CheckCircle2,
  Gift,
  Image as ImageIcon,
  RefreshCcw,
  Tag,
  Users,
} from "lucide-react";
import Modal from "../../components/ui/Modal";
import DynamicList from "../../components/dynamic/List";
import { ApiService } from "../../services/api";
import { isYesToolsHostname } from "../../utils/yesTools";

type CampaignRecord = {
  id?: string;
  name?: string;
  title?: string;
  description?: string | null;
  type?: string | null;
  isActive?: boolean;
  active?: boolean;
  joined?: boolean;
  isJoined?: boolean;
  participantEligible?: boolean;
  isEligible?: boolean;
  userEligible?: boolean;
  startDate?: string | null;
  endDate?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  percentage?: number | null;
  discountPercentage?: number | null;
  discountRate?: number | null;
  amount?: number | null;
  discountAmount?: number | null;
  fixedPrice?: number | null;
  minCartAmount?: number | null;
  minimumCartAmount?: number | null;
  requiredQuantity?: number | null;
  buyQuantity?: number | null;
  getQuantity?: number | null;
  categoryName?: string | null;
  productName?: string | null;
  metadata?: Record<string, any> | null;
  [key: string]: any;
};

const formatPrice = (value?: number | null) => {
  if (value === null || value === undefined || Number.isNaN(value)) return "";

  return new Intl.NumberFormat("tr-TR", {
    style: "currency",
    currency: "TRY",
    minimumFractionDigits: 2,
  }).format(value);
};

const formatDateRange = (startDate?: string | null, endDate?: string | null) => {
  const format = (value?: string | null) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";

    return date.toLocaleDateString("tr-TR", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const start = format(startDate);
  const end = format(endDate);

  if (start && end) return `${start} - ${end}`;
  return start || end || "";
};

const normalizeCampaigns = (response: any): CampaignRecord[] => {
  const candidates = [
    response?.data?.campaigns,
    response?.data?.rows,
    response?.data?.data?.campaigns,
    response?.data?.data,
    response?.data,
  ];

  const collection = candidates.find((item) => Array.isArray(item));
  return Array.isArray(collection) ? collection : [];
};

const translateCampaignType = (value?: string | null) => {
  switch (value) {
    case "percentage_discount":
      return "Yüzdelik İndirim";
    case "quantity_discount":
      return "Adet Kampanyası";
    case "cart_min_discount":
      return "Sepet Tutarı Kampanyası";
    case "combo_deal":
      return "Kombin Kampanyası";
    case "happy_hour":
      return "Saat Kampanyası";
    case "category_discount":
      return "Kategori İndirimi";
    case "fixed_product_price":
      return "Sabit Ürün Fiyatı";
    default:
      return value
        ? value
            .replace(/_/g, " ")
            .replace(/\b\w/g, (char) => char.toLocaleUpperCase("tr-TR"))
        : "Kampanya";
  }
};

const getCampaignTitle = (campaign: CampaignRecord) =>
  campaign.name || campaign.title || "Kampanya";

const getCampaignDescription = (campaign: CampaignRecord) =>
  campaign.description ||
  campaign.shortDescription ||
  campaign.content ||
  campaign.details ||
  campaign.metadata?.description ||
  "";

const getCampaignImageUrl = (campaign: CampaignRecord) =>
  campaign.imageUrl ||
  campaign.coverImageUrl ||
  campaign.coverUrl ||
  campaign.photoUrl ||
  campaign.bannerUrl ||
  campaign.thumbnailUrl ||
  campaign.media?.url ||
  campaign.image?.url ||
  campaign.coverImage?.url ||
  campaign.metadata?.imageUrl ||
  campaign.metadata?.coverImageUrl ||
  null;

const getCampaignStatusLabel = (campaign: CampaignRecord) => {
  if (campaign.joined === true || campaign.isJoined === true) {
    return "Katılım var";
  }

  if (
    campaign.participantEligible === true ||
    campaign.isEligible === true ||
    campaign.userEligible === true
  ) {
    return "Uygun";
  }

  if (campaign.isActive === false || campaign.active === false) {
    return "Pasif";
  }

  return "Aktif";
};

const getCampaignHighlights = (campaign: CampaignRecord) => {
  const items: string[] = [];
  const percentage =
    campaign.percentage ?? campaign.discountPercentage ?? campaign.discountRate ?? null;
  const amount = campaign.amount ?? campaign.discountAmount ?? null;
  const fixedPrice = campaign.fixedPrice ?? null;
  const minCartAmount = campaign.minCartAmount ?? campaign.minimumCartAmount ?? null;
  const requiredQuantity = campaign.requiredQuantity ?? campaign.buyQuantity ?? null;
  const freeQuantity = campaign.getQuantity ?? null;

  if (percentage !== null && percentage !== undefined) {
    items.push(`%${percentage} indirim`);
  }

  if (amount !== null && amount !== undefined) {
    const formatted = formatPrice(amount);
    if (formatted) items.push(`${formatted} indirim`);
  }

  if (fixedPrice !== null && fixedPrice !== undefined) {
    const formatted = formatPrice(fixedPrice);
    if (formatted) items.push(`Sabit fiyat ${formatted}`);
  }

  if (minCartAmount !== null && minCartAmount !== undefined) {
    const formatted = formatPrice(minCartAmount);
    if (formatted) items.push(`En az sepet ${formatted}`);
  }

  if (requiredQuantity !== null && requiredQuantity !== undefined) {
    items.push(`${requiredQuantity} adet koşulu`);
  }

  if (freeQuantity !== null && freeQuantity !== undefined) {
    items.push(`${freeQuantity} adet avantaj`);
  }

  if (campaign.categoryName) {
    items.push(campaign.categoryName);
  }

  if (campaign.productName) {
    items.push(campaign.productName);
  }

  return items.slice(0, 4);
};

function CampaignCard({ campaign, viewMode }: { campaign: CampaignRecord; viewMode?: string }) {
  const dateRange = formatDateRange(campaign.startDate, campaign.endDate);
  const statusLabel = getCampaignStatusLabel(campaign);
  const highlights = getCampaignHighlights(campaign);
  const imageUrl = getCampaignImageUrl(campaign);
  const description = getCampaignDescription(campaign);

  if (viewMode === "grid") {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 hover:bg-gray-100 transition-colors h-full">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-semibold text-gray-900 line-clamp-2">
              {getCampaignTitle(campaign)}
            </h3>
            <p className="mt-1 text-xs text-gray-500 line-clamp-2">
              {description || "Size uygun kampanya detayları burada listelenir."}
            </p>
          </div>

          {imageUrl ? (
            <img
              src={imageUrl}
              alt={getCampaignTitle(campaign)}
              className="w-14 h-14 rounded-lg border border-gray-200 object-cover flex-shrink-0 bg-white"
            />
          ) : (
            <div className="w-10 h-10 rounded-lg bg-white border border-gray-200 flex items-center justify-center flex-shrink-0">
              <Gift className="w-5 h-5 text-orange-600" />
            </div>
          )}
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <span className="inline-flex items-center gap-1 rounded-full bg-orange-50 border border-orange-200 px-2.5 py-1 text-xs font-medium text-orange-700">
            <BadgePercent className="w-3.5 h-3.5" />
            {translateCampaignType(campaign.type)}
          </span>
          <span className="inline-flex items-center gap-1 rounded-full bg-green-50 border border-green-200 px-2.5 py-1 text-xs font-medium text-green-700">
            <CheckCircle2 className="w-3.5 h-3.5" />
            {statusLabel}
          </span>
        </div>

        {highlights.length > 0 && (
          <div className="mt-4 space-y-2">
            {highlights.map((item) => (
              <div
                key={`${campaign.id || getCampaignTitle(campaign)}-${item}`}
                className="rounded-md bg-white border border-gray-200 px-3 py-2 text-xs text-gray-700"
              >
                {item}
              </div>
            ))}
          </div>
        )}

        {dateRange && (
          <div className="mt-4 flex items-center gap-2 text-xs text-gray-500">
            <CalendarRange className="w-3.5 h-3.5" />
            <span>{dateRange}</span>
          </div>
        )}

        {description && (
          <div className="mt-4 text-xs font-medium text-orange-700">
            Devamını Oku
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 hover:bg-gray-100 transition-colors">
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-start gap-3">
            {imageUrl ? (
              <img
                src={imageUrl}
                alt={getCampaignTitle(campaign)}
                className="w-16 h-16 rounded-lg bg-white border border-gray-200 object-cover flex-shrink-0"
              />
            ) : (
              <div className="w-12 h-12 rounded-lg bg-white border border-gray-200 flex items-center justify-center flex-shrink-0">
                <Gift className="w-5 h-5 text-orange-600" />
              </div>
            )}

            <div className="min-w-0 flex-1">
              <h3 className="text-base font-semibold text-gray-900">
                {getCampaignTitle(campaign)}
              </h3>
              <p className="mt-1 text-sm text-gray-500 line-clamp-2 whitespace-pre-line">
                {description || "Size uygun kampanya detayları burada listelenir."}
              </p>

              <div className="mt-3 flex flex-wrap gap-2">
                <span className="inline-flex items-center gap-1 rounded-full bg-orange-50 border border-orange-200 px-2.5 py-1 text-xs font-medium text-orange-700">
                  <Tag className="w-3.5 h-3.5" />
                  {translateCampaignType(campaign.type)}
                </span>
                <span className="inline-flex items-center gap-1 rounded-full bg-green-50 border border-green-200 px-2.5 py-1 text-xs font-medium text-green-700">
                  <Users className="w-3.5 h-3.5" />
                  {statusLabel}
                </span>
              </div>
            </div>
          </div>

          {highlights.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {highlights.map((item) => (
                <span
                  key={`${campaign.id || getCampaignTitle(campaign)}-${item}`}
                  className="inline-flex items-center rounded-md bg-white border border-gray-200 px-3 py-1.5 text-xs text-gray-700"
                >
                  {item}
                </span>
              ))}
            </div>
          )}

          {description && (
            <div className="mt-4 text-sm font-medium text-orange-700">
              Devamını Oku
            </div>
          )}
        </div>

        {dateRange && (
          <div className="w-full lg:w-56 rounded-lg bg-white border border-gray-200 px-3 py-3 text-sm text-gray-600">
            <div className="flex items-center gap-2 text-gray-500">
              <CalendarRange className="w-4 h-4" />
              <span>Kampanya dönemi</span>
            </div>
            <div className="mt-2 font-medium text-gray-800">{dateRange}</div>
          </div>
        )}
      </div>
    </div>
  );
}

function CampaignDetailModal({
  campaign,
  onClose,
}: {
  campaign: CampaignRecord | null;
  onClose: () => void;
}) {
  const imageUrl = campaign ? getCampaignImageUrl(campaign) : null;
  const description = campaign ? getCampaignDescription(campaign) : "";
  const dateRange = campaign ? formatDateRange(campaign.startDate, campaign.endDate) : "";
  const highlights = campaign ? getCampaignHighlights(campaign) : [];
  const statusLabel = campaign ? getCampaignStatusLabel(campaign) : "";

  return (
    <Modal
      isOpen={Boolean(campaign)}
      onClose={onClose}
      title={campaign ? getCampaignTitle(campaign) : ""}
      size="lg"
    >
      {campaign && (
        <div className="space-y-5">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={getCampaignTitle(campaign)}
              className="w-full max-h-80 rounded-xl border border-gray-200 object-cover bg-gray-100"
            />
          ) : (
            <div className="w-full h-48 rounded-xl border border-dashed border-gray-300 bg-gray-50 flex items-center justify-center text-gray-400">
              <div className="flex items-center gap-2 text-sm">
                <ImageIcon className="w-4 h-4" />
                <span>Kapak görseli bulunamadı</span>
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            <span className="inline-flex items-center gap-1 rounded-full bg-orange-50 border border-orange-200 px-2.5 py-1 text-xs font-medium text-orange-700">
              <Tag className="w-3.5 h-3.5" />
              {translateCampaignType(campaign.type)}
            </span>
            <span className="inline-flex items-center gap-1 rounded-full bg-green-50 border border-green-200 px-2.5 py-1 text-xs font-medium text-green-700">
              <Users className="w-3.5 h-3.5" />
              {statusLabel}
            </span>
            {dateRange && (
              <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 border border-blue-200 px-2.5 py-1 text-xs font-medium text-blue-700">
                <CalendarRange className="w-3.5 h-3.5" />
                {dateRange}
              </span>
            )}
          </div>

          {highlights.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {highlights.map((item) => (
                <span
                  key={`${campaign.id || getCampaignTitle(campaign)}-detail-${item}`}
                  className="inline-flex items-center rounded-md bg-gray-50 border border-gray-200 px-3 py-1.5 text-xs text-gray-700"
                >
                  {item}
                </span>
              ))}
            </div>
          )}

          <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-4">
            <div className="text-sm font-medium text-gray-900 mb-2">Açıklama</div>
            <div className="text-sm text-gray-700 whitespace-pre-wrap break-words leading-6">
              {description || "Bu kampanya için detay açıklama bulunamadı."}
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}

export default function CafeCampaignsPage() {
  const isYesToolsDomain =
    typeof window !== "undefined" && isYesToolsHostname(window.location.hostname);
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedCampaign, setSelectedCampaign] = useState<CampaignRecord | null>(null);

  const actions = useMemo(
    () => (
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => setRefreshKey((prev) => prev + 1)}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 text-sm font-medium"
        >
          <RefreshCcw className="w-4 h-4" />
          Yenile
        </button>
      </div>
    ),
    []
  );

  const fetcher = useCallback(async (_page: number, searchTerm?: string) => {
    const response = await ApiService.getCafeCampaigns();
    const campaigns = normalizeCampaigns(response);
    const normalizedSearch = String(searchTerm || "").trim().toLocaleLowerCase("tr-TR");

    const filteredCampaigns = normalizedSearch
      ? campaigns.filter((campaign) => {
          const haystack = [
            campaign.name,
            campaign.title,
            campaign.description,
            campaign.type,
            campaign.categoryName,
            campaign.productName,
          ]
            .filter(Boolean)
            .join(" ")
            .toLocaleLowerCase("tr-TR");

          return haystack.includes(normalizedSearch);
        })
      : campaigns;

    return {
      data: filteredCampaigns,
      meta: {
        page: 1,
        totalPages: 1,
        totalItems: filteredCampaigns.length,
        limit: filteredCampaigns.length || 1,
      },
    };
  }, []);

  if (!isYesToolsDomain) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="h-[calc(100dvh-4rem)]">
      <DynamicList
        type="list"
        header={false}
        title="Cafe Kampanyaları"
        path="cafe-campaigns"
        fetcher={fetcher}
        refreshKey={refreshKey}
        isSearch
        isPagination={false}
        showToggle
        actions={actions}
        emptyMessage="Size uygun kampanya bulunamadı."
        errorMessage="Cafe kampanyaları yüklenirken bir hata oluştu."
        item={(campaign: CampaignRecord, viewMode?: string) => (
          <button
            key={campaign.id || getCampaignTitle(campaign)}
            type="button"
            onClick={() => setSelectedCampaign(campaign)}
            className="w-full text-left"
          >
            <CampaignCard campaign={campaign} viewMode={viewMode} />
          </button>
        )}
      />
      <CampaignDetailModal
        campaign={selectedCampaign}
        onClose={() => setSelectedCampaign(null)}
      />
    </div>
  );
}
